/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { XIcon } from "lucide-react"
import { useState } from "react"

import { availableDeviceFlags, parseDeviceFlags } from "../../features/application/device-flags"
import { Badge } from "../primitives/badge"
import { Button } from "../primitives/button"
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "../primitives/input-group"
import { NativeSelect, NativeSelectOption } from "../primitives/native-select"

interface Props {
    /**
     * Flags currently applied to the device list.
     *
     * Stored in the URL so reloads and shared links preserve the filter. Applied
     * flags are shown as removable badges and excluded from the dropdown.
     */
    selectedFlags: string[]
    /**
     * Called with the complete set of flags after a change.
     *
     * The view writes the new set to the URL and reloads from the first page,
     * since changing the filter can change the number and order of pages.
     */
    onFlagsChange: (flags: string[]) => void
}

/**
 * Filters the insecure device list by device flags.
 *
 * Known flags are available from the dropdown; the text field also accepts
 * arbitrary flags and comma-separated values. Both paths use `parseDeviceFlags`,
 * so whitespace and casing are normalized consistently.
 *
 * Render this filter only for the insecure device list; other statuses do not
 * have flags.
 *
 * @example
 * <ApplicationListFlagFilter selectedFlags={vm.selectedPageId?.flags ?? []} onFlagsChange={handleFlags} />
 */
export const ApplicationListFlagFilter = ({ selectedFlags, onFlagsChange }: Props) => {
    // Hold flags that have been typed but not added to the filter yet.
    const [draft, setDraft] = useState("")

    const addFlags = (raw: string) => {
        const added = parseDeviceFlags(raw).filter((flag) => !selectedFlags.includes(flag))

        if (added.length > 0) {
            onFlagsChange([...selectedFlags, ...added])
        }
    }

    const commitDraft = () => {
        addFlags(draft)
        setDraft("")
    }

    const removeFlag = (flag: string) => onFlagsChange(selectedFlags.filter((selected) => selected !== flag))

    const clearFlags = () => onFlagsChange([])

    /*
     * Enable Add only when the draft contains at least one new flag. This same
     * check guards Enter, so keyboard and button submission behave identically.
     */
    const canCommitDraft = parseDeviceFlags(draft).some((flag) => !selectedFlags.includes(flag))

    return (
        <fieldset className="m-0 mb-6 grid gap-2 p-0">
            <legend className="m-0 text-sm font-bold uppercase">Filter</legend>

            <div className="flex flex-wrap gap-2">
                {/*
                 * Selecting a flag adds it immediately, so the select always
                 * returns to its placeholder instead of holding an applied flag.
                 */}
                <NativeSelect
                    className="bg-background"
                    aria-label="Add a flag"
                    value=""
                    onChange={(e) => addFlags(e.target.value)}
                >
                    <NativeSelectOption value="">Select flags...</NativeSelectOption>
                    {availableDeviceFlags(selectedFlags).map((flag) => (
                        <NativeSelectOption key={flag} value={flag}>
                            {flag}
                        </NativeSelectOption>
                    ))}
                </NativeSelect>

                <InputGroup className="w-64 bg-background">
                    <InputGroupInput
                        aria-label="Custom flag"
                        placeholder="Custom flags, comma separated"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && canCommitDraft) commitDraft()
                        }}
                    />
                    <InputGroupAddon align="inline-end">
                        <InputGroupButton
                            variant="outline"
                            className="uppercase"
                            disabled={!canCommitDraft}
                            onClick={commitDraft}
                        >
                            Add
                        </InputGroupButton>
                    </InputGroupAddon>
                </InputGroup>

                {/*
                 * Show Clear all only when there is something to clear. Keep it
                 * visually lighter than the two controls that add filters.
                 */}
                {selectedFlags.length > 0 && (
                    <Button variant="ghost" className="animate-in uppercase fade-in" onClick={clearFlags}>
                        Clear all
                    </Button>
                )}
            </div>

            {selectedFlags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selectedFlags.map((flag) => (
                        <Badge
                            key={flag}
                            className="animate-in fade-in"
                            render={<button type="button" onClick={() => removeFlag(flag)} />}
                            aria-label={`Remove ${flag}`}
                        >
                            {flag}
                            <XIcon data-icon="inline-end" />
                        </Badge>
                    ))}
                </div>
            )}
        </fieldset>
    )
}
